import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getFarmById } from 'farms/selectors';
import {
  getJohnDeereClients,
  getJohnDeereFields,
  getJohnDeereFieldsByClientName,
} from './selectors';
import { isFvFieldSelected } from 'export-selector/selectors';
import { isFieldsFromOlEnabled } from 'user/selectors';
import { setFVFieldForJohnDeereField } from 'affiliates';
import {
  removeFVFieldForWorkPlans,
  toggleFVFieldForWorkPlans,
} from 'export-selector';
import { convertForLocale } from 'util/unitConversion';
import { getMessage } from 'util/locale';

import { Dropdown } from '@tcc/crate';
import CheckboxRadio from 'common/CheckboxRadio';
import { FieldArea, FieldFarm, Store } from 'store/types/types';

interface WorkPlansFieldsSelectorRowProps {
  area: FieldArea;
  farmId: string;
  fieldsAmount: number;
  uuid: string;
  name: string;
  farm: FieldFarm;
}

const WorkPlansFieldsSelectorRow = ({
  area,
  farmId,
  fieldsAmount,
  uuid,
  name: fieldName,
  farm,
}: WorkPlansFieldsSelectorRowProps) => {
  const convertedArea = convertForLocale({
    quantity: area.q,
    unit: area.u,
    unitType: 'area',
  }).toString();
  const dispatch = useDispatch();
  const defaultClient = {
    id: 'All clients',
    name: getMessage('john-deere-all-clients'),
  };
  const [selectedClient, setSelectedClient] = useState(defaultClient);
  const { growerName, name }
    = useSelector((state: Store) => getFarmById(state, farmId)) || {};
  const johnDeereFields = useSelector(getJohnDeereFields);
  const johnDeereClients = useSelector(getJohnDeereClients);
  const johnDeereFieldsFilteredByClient = useSelector((state: Store) =>
    getJohnDeereFieldsByClientName(state, selectedClient?.name),
  );
  const isFieldSelected = useSelector((state: Store) =>
    isFvFieldSelected(state, uuid),
  );
  const areFieldsFromOl = useSelector(isFieldsFromOlEnabled);
  const client = areFieldsFromOl ? farm.growerName : growerName;
  const farmName = areFieldsFromOl ? farm.name : name;
  const selectedJohnDeereField = johnDeereFields.find(
    ({ fvFieldUuid }) => fvFieldUuid === uuid,
  );
  const clientOptions = [defaultClient, ...johnDeereClients];
  const fieldsOptions
    = selectedClient?.id === defaultClient.id || johnDeereClients.length < 2
      ? johnDeereFields
      : johnDeereFieldsFilteredByClient;

  const setClient = (_: unknown, clientId: string) => {
    if (
      selectedJohnDeereField
      && ![defaultClient.id, selectedClient.id].includes(clientId)
      && selectedJohnDeereField.clientName !== clientId
    ) {
      dispatch(removeFVFieldForWorkPlans(uuid));
      dispatch(
        setFVFieldForJohnDeereField({
          fvFieldUuid: null,
          johnDeereFieldId: selectedJohnDeereField.id,
        }),
      );
    }
    setSelectedClient({ id: clientId, name: clientId });
  };

  const selectJohnDeereField = (_: unknown, johnDeereFieldId: string) => {
    if (fieldsAmount === 1 && !isFieldSelected) {
      dispatch(toggleFVFieldForWorkPlans(uuid));
    }
    dispatch(
      setFVFieldForJohnDeereField({ fvFieldUuid: uuid, johnDeereFieldId }),
    );
  };

  const toggleField = () => {
    dispatch(toggleFVFieldForWorkPlans(uuid));
  };

  const fieldCheckbox
    = fieldsAmount > 1 ? (
      <td>
        <CheckboxRadio
          disabled={!selectedJohnDeereField}
          isChecked={isFieldSelected}
          onChange={toggleField}
          type="checkbox"
        />
      </td>
    ) : null;

  return (
    <tr>
      <td>
        <p>{fieldName}</p>
        <small>{`${convertedArea} • ${client} • ${farmName}`}</small>
      </td>
      <td>
        <Dropdown
          disabled={johnDeereClients.length < 2}
          options={clientOptions}
          setValue={setClient}
          selectedId={selectedClient?.id}
          searchable={clientOptions.length > 7}
          searchPlaceholder={getMessage('placeholder-text__search')}
          testId={'john-deere-client-selector'}
        />
      </td>
      <td>
        <Dropdown
          options={fieldsOptions}
          setValue={selectJohnDeereField}
          selectedId={selectedJohnDeereField?.id}
          isLoading={!fieldsOptions?.length}
          placeholder={getMessage('placeholder-text__select-field')}
          searchable={fieldsOptions?.length > 6}
          searchPlaceholder={getMessage('placeholder-text__search')}
          testId={'john-deere-field-selector'}
        />
      </td>
      {fieldCheckbox}
    </tr>
  );
};

export default WorkPlansFieldsSelectorRow;
